import { CommonModule } from "@angular/common";
import { Component, computed, inject, input, OnDestroy, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router, RouterModule } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { environment } from "../../../core/environments/environment";
import { AdaaHelper } from "../../../core/utils";
import { noWhitespaceValidator } from "../../../core/validators";
import { AuthLayoutComponent, FormInputComponent } from "../../../shared/components";
import { Constants } from "../../../shared/constants/constants";
import { Language, UaePassErrors } from "../../../shared/constants/enums";
import { LoginRequest, LoginResponse, MainResponseModel } from "../../../shared/models";
import {
  LanguageService,
  LoaderService,
  LoginService,
  RecaptchaService,
  UserProfileApiService,
} from "../../../shared/services";

@Component({
  selector: "adaa-login",
  templateUrl: "./login.html",
  styleUrl: "./login.scss",
  imports: [CommonModule, TranslatePipe, FormInputComponent, ReactiveFormsModule, RouterModule, AuthLayoutComponent],
})
export class LoginComponent implements OnInit, OnDestroy {
  languageService = inject(LanguageService);
  private _formBuilder = inject(FormBuilder);
  private _router = inject(Router);
  private _translateService = inject(TranslateService);
  private _toastrService = inject(ToastrService);
  private _loginService = inject(LoginService);
  private _userProfileApiService = inject(UserProfileApiService);
  private _loaderService = inject(LoaderService);
  private _recaptchaService = inject(RecaptchaService);

  loginForm: FormGroup;
  showUaePass = environment.uaePass.enable;
  readonly showPassword = signal<boolean>(false);

  uaePassCode = input<string>(undefined, { alias: "code" });
  uaePassError = input<string>(undefined, { alias: "error_description" });
  redirectUrl = input<string | undefined>(undefined);

  isArabic = computed<boolean>(() => this.languageService.current() === Language.Arabic);

  public ngOnInit(): void {
    //ONLY IF THERE IS NOT UAEPASSCODE
    //IF WE HAVE UAE PASS CODE THEN WE NEED TO KEEP THE VALUE SO THE USER CAN NAVIGATE TO THE LAST URL BEFORE SIGN IN UAE PASS SHOW
    if (!this.uaePassCode() && !this.uaePassError()) {
      AdaaHelper.isDefinedAndNotEmpty(this.redirectUrl())
        ? AdaaHelper.setLocalStorage(Constants.localStorageKeys.redirectUrl, this.redirectUrl(), "string")
        : AdaaHelper.deleteLocalStorage(Constants.localStorageKeys.redirectUrl);
    }

    this._checkForSessionExpiredError();
    this._recaptchaService.appendReCAPTCHA();

    this._prepareForm();

    if (this.uaePassCode()) this._handleUaePassLogin(this.uaePassCode()!);
    else if (this.uaePassError()) this._handleUaePassError(this.uaePassError()!);
  }

  public ngOnDestroy(): void {
    this._toastrService.clear();
  }

  public submit() {
    this._recaptchaService.getToken("login", (token) => {
      this.loginForm.get("captchaToken")?.setValue(token);

      if (!this.loginForm.valid) return;

      this._loginService.login(this.loginForm.value as LoginRequest).subscribe((response: MainResponseModel) => {
        const res = response.responseData as LoginResponse;
        if (response.inError) {
          this._toastrService.error(this._translateService.instant("notification.error.code_" + response.responseCode));
          return;
        }

        if (response.responseCode === "1") {
          // Set a default entity for the user (Helps for first routing)
          //   this.storeService.entityId = res.entityId.toString();
          // const defaultEntity: any = { entity: { id: res.entityId } };
          //   this.store.dispatch(selected_entity(defaultEntity));

          this._checkVerificationCode(res);
        }
      });
    });
  }

  public loginUaePass() {
    const language = this.isArabic() ? "ar" : "en";

    const url = `${environment.uaePass.url}?response_type=code&client_id=${environment.uaePass.clientId}&scope=${environment.uaePass.scope}&state=HnlHOJTkTb66Y5H&redirect_uri=${environment.uaePass.loginUrl}&acr_values=urn:safelayer:tws:policies:authentication:level:low&ui_locales=${language}`;

    window.location.href = url;
  }

  /**
   * @description The BE decides whether the OTP step is needed. It returns `secondStepValidated: true`
   * when the session is already validated (trusted device or UAE PASS), otherwise route to /verification
   * @param res
   */
  private _checkVerificationCode(res: LoginResponse) {
    const secondStepValidated = res.secondStepValidated === true;

    AdaaHelper.setLocalStorage(Constants.localStorageKeys.sessionId, res.sessionId, "string");
    AdaaHelper.setLocalStorage(Constants.localStorageKeys.currentEntity, { id: res.entityId }, "json");

    if (secondStepValidated) {
      //Used after login to check page
      AdaaHelper.setLocalStorage(Constants.localStorageKeys.checkLogin, "1", "string");

      //User to get the current user info in the system
      AdaaHelper.setLocalStorage(Constants.localStorageKeys.user, res, "json");
    }

    this._onSuccessfulLogin(res, secondStepValidated);
  }

  private _prepareForm() {
    this.loginForm = this._formBuilder.group({
      captchaToken: [null, Validators.required],
      email: [null, [Validators.required, Validators.email]],
      password: [null, [Validators.required, noWhitespaceValidator()]],
      token: [""],
      uuid: [""],
    });
  }

  private _onSuccessfulLogin(user: LoginResponse, secondStepValidated: boolean) {
    const clearRelevantStorage = () => {
      AdaaHelper.deleteLocalStorage(Constants.localStorageKeys.firstVisit);
      AdaaHelper.deleteLocalStorage(Constants.localStorageKeys.lastUser);
      AdaaHelper.deleteLocalStorage(Constants.localStorageKeys.lastUrlVisited);
      AdaaHelper.deleteLocalStorage(Constants.localStorageKeys.systemTour);
    };

    this._userProfileApiService.getSecurityQuestion().subscribe({
      next: (response) => {
        //User Need Verification - OTP
        if (response.inError || !secondStepValidated) {
          clearRelevantStorage();
          return this._router.navigateByUrl("/verification", { state: { user } });
        }

        return this._loginService.doLogin(response.responseData.length > 0, user);
      },
    });
  }

  private _handleUaePassLogin(code: string) {
    this._loginService.uaePassLogin(code).subscribe({
      next: (response) => {
        if (response.responseCode === "63") {
          this._router.navigateByUrl(`/link-account/${response.responseData.uuid}`);
        } else {
          if (response.inError) {
            this._endUaePassSession(response.responseCode);
            return;
          }

          AdaaHelper.setLocalStorage(Constants.localStorageKeys.uaePassLogin, true, "string");
          this._checkVerificationCode(response.responseData);
        }
      },
    });
  }

  private _handleUaePassError(errorCode: string) {
    let errorMessage: string | undefined = undefined;

    if (errorCode === UaePassErrors.CANCELLED_ON_WEB) errorMessage = "notification.error.code_68";
    else if (errorCode === UaePassErrors.CANCELLED_ON_APP) errorMessage = "notification.error.code_72";
    else if (errorCode === UaePassErrors.USER_CLICKED_ON_EXIT) errorMessage = "notification.error.code_69";

    if (errorMessage)
      this._translateService.get(errorMessage).subscribe({
        next: (result) => {
          this._toastrService.error(result, undefined, { timeOut: 10000 });
        },
      });
  }

  private _endUaePassSession(responseCode: string) {
    const errorCodes = ["65", "66", "67", "68", "69", "70", "71", "72"];

    if (errorCodes.includes(responseCode)) {
      this._loaderService.start();
      this._loginService.uaePassLogout();
    }
  }

  private _checkForSessionExpiredError() {
    const isSessionExpired = AdaaHelper.getLocalStorage(Constants.localStorageKeys.sessionExpired) === "true";
    if (!isSessionExpired) return;

    setTimeout(() => {
      AdaaHelper.deleteLocalStorage(Constants.localStorageKeys.sessionExpired);
      this._translateService.get("notification.error.session_expired").subscribe((res: string) => {
        this._toastrService.warning(res, undefined, {
          disableTimeOut: true,
          closeButton: true,
        });
      });
    }, 3000);
  }
}
